package io.github.erkko68.filament

import io.github.erkko68.filament.capi.*
import io.github.erkko68.filament.interop.*

/**
 * IndirectLight simulates environment lighting, a form of global illumination.
 *
 * Environment lighting provides two components:
 * - **Irradiance:** Diffuse lighting from the environment (brightness contribution from all directions)
 * - **Reflections:** Specular component (mirror-like reflections from the environment on shiny surfaces)
 *
 * Environments are typically captured as high-resolution HDR equirectangular images and processed
 * by the **cmgen** tool to generate the data needed by IndirectLight.
 *
 * **Creation and destruction:**
 * Create an IndirectLight using the Builder and assign it to the Scene via Scene.setIndirectLight().
 * Destroy with Engine.destroy(). Only one IndirectLight can be active in a Scene at a time.
 *
 * **Irradiance:**
 * The irradiance represents light coming from the environment. It is calculated automatically
 * from the reflections cubemap, but can be provided separately as spherical harmonics (SH)
 * coefficients for memory efficiency.
 *
 * **Spherical harmonics:**
 * Irradiance and radiance can be specified as SH coefficients. Use 1, 2, or 3 bands for 1, 4, or 9
 * coefficients respectively. The **cmgen** tool generates pre-scaled SH coefficients that are ready to use.
 *
 * **Reflections:**
 * Reflections on object surfaces are calculated from a specially filtered cubemap pyramid
 * generated by the **cmgen** tool. Each mipmap level encodes irradiance for a different roughness level.
 *
 * @see Scene
 * @see Texture
 * @see Skybox
 */
class IndirectLight @InternalFilamentApi constructor(internal var nativeHandle: NativePointer) {
    /** The native object, for interop with code calling the Fila* C API directly. Read-only: this wrapper owns it. */
    @InternalFilamentApi
    val nativeObject: NativePointer get() = nativeHandle

    /**
     * Builder for creating IndirectLight instances.
     *
     * Set reflections and/or irradiance, configure intensity and rotation, then call build().
     */
    class Builder() {
        private val nativeBuilder = FilaIndirectLightBuilder_create()

        /**
         * Sets the reflections cubemap mipmap chain.
         *
         * The cubemap must be generated by the **cmgen** tool. Each mipmap level encodes
         * the irradiance for a different roughness level.
         *
         * @param cubemap Mip-mapped cubemap (or null to clear)
         * @return This Builder, for chaining calls
         */
        fun reflections(cubemap: Texture?): Builder = apply { FilaIndirectLightBuilder_reflections(nativeBuilder, cubemap?.nativeHandle ?: NullPointer) }
        /**
         * Sets the irradiance from pre-convolved spherical harmonics coefficients.
         *
         * Coefficients must already be pre-scaled by the reconstruction factors and
         * the Lambertian diffuse BRDF (1/π). Generate with the **cmgen** tool.
         *
         * @param bands Number of SH bands (1, 2, or 3)
         * @param sh Array of 3D coefficients; size = bands² (1, 4, or 9 float3 values)
         * @return This Builder, for chaining calls
         *
         * @see Material.Builder.sphericalHarmonicsBandCount
         */
        fun irradiance(bands: Int, sh: FloatArray): Builder = apply {
            sh.usePinned(bands * bands * 3) { pinned ->
                FilaIndirectLightBuilder_irradiance_uint8_t_float3(nativeBuilder, bands, pinned)
            }
        }
        /**
         * Sets the irradiance computed from radiance spherical harmonics coefficients.
         *
         * Coefficients are raw radiance values (not pre-convolved). The builder
         * automatically convolves them and applies the Lambertian BRDF.
         *
         * @param bands Number of SH bands (1, 2, or 3)
         * @param sh Array of 3D coefficients; size = bands² (1, 4, or 9 float3 values)
         * @return This Builder, for chaining calls
         */
        fun radiance(bands: Int, sh: FloatArray): Builder = apply {
            sh.usePinned(bands * bands * 3) { pinned ->
                FilaIndirectLightBuilder_radiance(nativeBuilder, bands, pinned)
            }
        }
        /**
         * Sets the irradiance cubemap (for diffuse lighting only).
         *
         * If not provided, irradiance is computed automatically from the reflections cubemap.
         *
         * @param cubemap Cubemap texture for irradiance (or null to clear)
         * @return This Builder, for chaining calls
         */
        fun irradiance(cubemap: Texture?): Builder = apply { FilaIndirectLightBuilder_irradiance_Texture(nativeBuilder, cubemap?.nativeHandle ?: NullPointer) }
        /**
         * Sets the environment's overall intensity multiplier.
         *
         * This scales the contribution of both irradiance and reflections.
         * Default: 1.0.
         *
         * @param envIntensity Intensity multiplier (e.g., 0.5 to dim, 2.0 to brighten)
         * @return This Builder, for chaining calls
         */
        fun intensity(envIntensity: Float): Builder = apply { FilaIndirectLightBuilder_intensity(nativeBuilder, envIntensity) }
        /**
         * Rotates the environment mapping.
         *
         * Allows repositioning of the environment without re-processing the cubemaps.
         *
         * @param rotation 3x3 rotation matrix as a 9-element array (or identity if not set)
         * @return This Builder, for chaining calls
         */
        fun rotation(rotation: FloatArray): Builder = apply {
            rotation.usePinned(9) { pinned ->
                FilaIndirectLightBuilder_rotation(nativeBuilder, pinned)
            }
        }
        /**
         * Creates the IndirectLight object.
         *
         * @param engine Engine to associate this IndirectLight with
         * @return The newly created IndirectLight
         */
        fun build(engine: Engine): IndirectLight {
            val handle = FilaIndirectLightBuilder_build(nativeBuilder, engine.nativeHandle)
            FilaIndirectLightBuilder_destroy(nativeBuilder)
            return IndirectLight(handle)
        }
    }

    /**
     * Gets/sets the environment's overall intensity multiplier.
     *
     * Scales the contribution of both irradiance and reflections. Default: 1.0.
     */
    var intensity: Float
        get() = FilaIndirectLight_getIntensity(nativeHandle)
        set(value) { FilaIndirectLight_setIntensity(nativeHandle, value) }

    /**
     * Gets/sets the environment rotation as a 3x3 matrix.
     *
     * Allows dynamic repositioning without re-processing cubemaps.
     */
    var rotation: FloatArray
        get() = FloatArray(9).also { result -> result.usePinned(9) { FilaIndirectLight_getRotation(nativeHandle, it) } }
        set(value) { value.usePinned(9) { FilaIndirectLight_setRotation(nativeHandle, it) } }

    /**
     * Gets the reflections cubemap texture (if set).
     * @return The reflections cubemap, or null if not provided
     */
    val reflectionsTexture: Texture? get() = FilaIndirectLight_getReflectionsTexture(nativeHandle).takeIf { it != NullPointer }?.let(::Texture)
    /**
     * Gets the irradiance cubemap texture (if set).
     * @return The irradiance cubemap, or null if computed from reflections
     */
    val irradianceTexture: Texture? get() = FilaIndirectLight_getIrradianceTexture(nativeHandle).takeIf { it != NullPointer }?.let(::Texture)

    /**
     * Helper to estimate the direction of the dominant light in the environment.
     *
     * This assumes that there is only a single dominant light (such as the sun in outdoors
     * environments), if it's not the case the direction returned will be an average of the
     * various lights based on their intensity.
     *
     * If there are no clear dominant light, as is often the case with low dynamic range (LDR)
     * environments, this method may return a wrong or unexpected direction.
     *
     * The dominant light direction can be used to set a directional light's direction,
     * for instance to produce shadows that match the environment.
     *
     * @param out Optional FloatArray for result; created if null
     * @return A unit vector representing the direction of the dominant light
     *
     * @see LightManager.Builder.direction
     * @see getColorEstimate
     */
    fun getDirectionEstimate(out: FloatArray? = null): FloatArray =
        (out ?: FloatArray(3)).also { result -> result.usePinned(3) { FilaIndirectLight_getDirectionEstimate(nativeHandle, it) } }

    /**
     * Helper to estimate the color and relative intensity of the environment in a given direction.
     *
     * This can be used to set the color and intensity of a directional light. In this case
     * make sure to multiply the returned intensity by the intensity of this indirect light.
     *
     * @param x Direction X component (should form a unit vector with y and z)
     * @param y Direction Y component (should form a unit vector with x and z)
     * @param z Direction Z component (should form a unit vector with x and y)
     * @param out Optional FloatArray for result; created if null
     * @return A vector of 4 floats: first 3 components are the linear color, 4th is the intensity
     *
     * @see getDirectionEstimate
     */
    fun getColorEstimate(x: Float, y: Float, z: Float, out: FloatArray? = null): FloatArray {
        val result = out ?: FloatArray(4)
        floatArrayOf(x, y, z).usePinned(3) { pDir ->
            result.usePinned(4) { pOut -> FilaIndirectLight_getColorEstimate(nativeHandle, pDir, pOut) }
        }
        return result
    }

    companion object {
        init { Filament.init() } // statics are callable before any Engine exists
        /**
         * Helper to estimate the direction of the dominant light in the environment represented by
         * spherical harmonics.
         *
         * This assumes that there is only a single dominant light (such as the sun in outdoors
         * environments), if it's not the case the direction returned will be an average of the
         * various lights based on their intensity.
         *
         * If there are no clear dominant light, as is often the case with low dynamic range (LDR)
         * environments, this method may return a wrong or unexpected direction.
         *
         * The dominant light direction can be used to set a directional light's direction,
         * for instance to produce shadows that match the environment.
         *
         * @param sh 3-band spherical harmonics coefficients (must be 9 float3 values, or 27 floats if flattened)
         * @param out Optional FloatArray for result; created if null
         * @return A unit vector representing the direction of the dominant light
         *
         * @see LightManager.Builder.direction
         */
        fun getDirectionEstimate(sh: FloatArray, out: FloatArray? = null): FloatArray {
            val result = out ?: FloatArray(3)
            sh.usePinned(27) { pSh ->
                result.usePinned(3) { pOut ->
                    FilaIndirectLight_getDirectionEstimate_float3(pSh, pOut)
                }
            }
            return result
        }

        /**
         * Helper to estimate the color and relative intensity of the environment represented by
         * spherical harmonics in a given direction.
         *
         * This can be used to set the color and intensity of a directional light. In this case
         * make sure to multiply the returned intensity by the intensity of this indirect light.
         *
         * @param sh 3-band spherical harmonics coefficients (must be 9 float3 values, or 27 floats if flattened)
         * @param x Direction X component (should form a unit vector with y and z)
         * @param y Direction Y component (should form a unit vector with x and z)
         * @param z Direction Z component (should form a unit vector with x and y)
         * @param out Optional FloatArray for result; created if null
         * @return A vector of 4 floats: first 3 components are the linear color, 4th is the intensity
         *
         * @see getDirectionEstimate
         * @see LightManager.Builder.color
         * @see LightManager.Builder.intensity
         */
        fun getColorEstimate(sh: FloatArray, x: Float, y: Float, z: Float, out: FloatArray? = null): FloatArray {
            val result = out ?: FloatArray(4)
            sh.usePinned(27) { pSh ->
                floatArrayOf(x, y, z).usePinned(3) { pDir ->
                    result.usePinned(4) { pOut -> FilaIndirectLight_getColorEstimate_float3(pSh, pDir, pOut) }
                }
            }
            return result
        }
    }
}
